<template>
  <div class="mx-auto px-4 lg:px-6">
    <div class="flex h-16 justify-between">
      <div class="flex items-center">
        <div class="mr-4 flex items-center lg:hidden">
          <button
            type="button"
            class="z-10 -mx-4 inline-flex items-center justify-center p-4 focus:outline-none"
            aria-controls="mobile-menu"
            aria-expanded="false"
            @click="$emit('openMobile')"
          >
            <span class="sr-only">Open main menu</span>
            <BurgerIcon class="size-6 text-burnt-sienna" />
          </button>
        </div>
        <div class="shrink-0">
          <a href="https://pastaevangelists.com/" aria-label="Go to homepage" class="-mt-0.5">
            <ThePastaLogo class="h-auto w-[100px] cursor-pointer py-4 text-terracota-dark" />
          </a>
        </div>

        <div class="ml-12 hidden lg:-my-px lg:flex lg:space-x-8">
          <button
            v-for="navigationLink in links"
            :key="navigationLink.label"
            class="relative text-left text-burnt-sienna"
            @mouseover="hoverIn(navigationLink.label)"
            @mouseleave="hoverOut(navigationLink.label)"
            @focus="hoverIn(navigationLink.label)"
            @blur="hoverOut(navigationLink.label)"
          >
            <router-link
              v-if="navigationLink.name"
              :to="{ name: navigationLink.name }"
              class="inline-flex shrink-0 items-center"
            >
              {{ navigationLink.label }}
              <SolidChevronDownIcon v-if="navigationLink.children" class="ml-2 size-4" />
            </router-link>
            <a
              v-else-if="navigationLink.path"
              class="inline-flex items-center"
              :href="navigationLink.path"
              :target="navigationLink.children ? undefined : '_blank'"
            >
              {{ navigationLink.label }}
              <SolidChevronDownIcon v-if="navigationLink.children" class="mt-0.5 size-4" />
            </a>
            <div v-else class="inline-flex items-center">
              {{ navigationLink.label }}
              <SolidChevronDownIcon v-if="navigationLink.children" class="mt-0.5 size-4" />
            </div>
            <div
              v-if="navigationLink.children && isOpen(navigationLink.label)"
              class="absolute z-10 space-y-4 bg-sandstone py-6 pl-6 pr-12 shadow-sm"
            >
              <template v-for="childLink in navigationLink.children" :key="childLink.label">
                <a
                  v-if="'path' in childLink"
                  class="block whitespace-nowrap capitalize tracking-tighter text-burnt-sienna hover:underline"
                  :href="childLink.path"
                  target="_blank"
                >
                  {{ childLink.label }}
                </a>
                <router-link
                  v-else-if="'name' in childLink"
                  :to="{
                    name: childLink.name,
                    params:
                      'params' in childLink
                        ? (childLink.params as Record<string, string>)
                        : undefined,
                  }"
                  class="block whitespace-nowrap capitalize tracking-tighter text-burnt-sienna hover:underline"
                >
                  {{ childLink.label }}
                </router-link>
              </template>
            </div>
          </button>
        </div>
      </div>
      <div class="flex items-center lg:space-x-4">
        <router-link
          v-if="!hideShopNow"
          :to="{name: 'pasta-now-search-address'}"
          class="primary-cta--small mt-0.5 inline-flex px-4 text-sm lg:px-6 lg:text-base"
        >
          Order now
        </router-link>

        <slot name="extra-cta" />

        <div class="relative hidden lg:block">
          <button
            v-if="isLoggedIn"
            class="relative text-left"
            @mouseover="isMenuOpen.myAccount = true"
            @mouseleave="isMenuOpen.myAccount = false"
            @focus="isMenuOpen.myAccount = true"
            @blur="isMenuOpen.myAccount = false"
          >
            <router-link to="/account" class="inline-flex items-center pt-1">
              <div class="rounded-full bg-sandstone p-1">
                <UserIcon class="size-8 text-burnt-sienna group-hover:text-terracota-dark" />
              </div>
            </router-link>

            <div
              v-if="isMenuOpen.myAccount"
              class="absolute -left-36 z-10 space-y-4 bg-sandstone py-6 pl-6 pr-10 shadow-md"
            >
              <router-link
                to="/account"
                class="block whitespace-nowrap text-[17px] capitalize text-burnt-sienna hover:underline"
              >
                Go to my account
              </router-link>
              <button
                class="block whitespace-nowrap text-[17px] text-burnt-sienna hover:underline"
                @click="$emit('logout')"
              >
                Log out
              </button>
            </div>
          </button>

          <router-link
            v-if="!isLoggedIn && !isOnLoginOrSignup"
            to="/social-login"
            class="inline-flex items-center pt-1"
          >
            <div class="rounded-full bg-sandstone p-1">
              <UserIcon class="size-8 text-burnt-sienna group-hover:text-terracota-dark" />
            </div>
          </router-link>
          <div
            class="absolute -bottom-2 right-1 h-0.5 w-8 bg-burnt-sienna group-hover:block"
            :class="{ hidden: !activeIcon, block: activeIcon }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { experienceLinks, giftMenuLinks, storesLinks } from '@/constants/links';
  import { camelize } from 'humps';

  interface Props {
    isLoggedIn?: boolean;
    isOnLoginOrSignup?: boolean;
    hideShopNow?: boolean;
    activeIcon?: boolean;
  }

  interface Emits {
    (e: 'openMobile'): void;
    (e: 'logout'): void;
  }

  withDefaults(defineProps<Props>(), { isLoggedIn: false, isOnLoginOrSignup: false });
  defineEmits<Emits>();

  const isMenuOpen = reactive({
    gifts: false,
    experiences: false,
    myAccount: false,
    restaurants: false,
    restaurantMenus: false,
    bookATable: false,
  });

  const hoverIn = (label: string) => {
    const camelLabel = camelize(label);
    if (Object.keys(isMenuOpen).includes(camelLabel)) {
      const key = camelLabel as keyof typeof isMenuOpen;
      isMenuOpen[key] = true;
    }
  };

  const hoverOut = (label: string) => {
    const camelLabel = camelize(label);
    if (Object.keys(isMenuOpen).includes(camelLabel)) {
      const key = camelLabel as keyof typeof isMenuOpen;
      isMenuOpen[key] = false;
    }
  };

  const isOpen = (label: string) => {
    const camelLabel = camelize(label);
    return Object.keys(isMenuOpen).includes(camelLabel)
      ? isMenuOpen[camelLabel as keyof typeof isMenuOpen]
      : false;
  };

  const links = [
    {
      label: 'Restaurant Menus',
      name: 'restaurants',
      children: [
        {
          label: 'Restaurant menu',
          path: 'https://cdn.shopify.com/s/files/1/1725/5449/files/PE_TableMenu_all_LW.pdf?v=1761056106',
        },
        {
          label: 'Harrods menu',
          path: 'https://cdn.shopify.com/s/files/1/1725/5449/files/APR25_Booklet_inner_Harrods_menu_2025_2_1.pdf?v=1761056106',
        },
      ],
    },
    {
      label: 'Book a table',
      name: 'restaurants',
      children: storesLinks,
    },
    { label: 'Delivery', name: 'pasta-now-search-address' },
    {
      label: 'Experiences',
      path: 'https://pastaevangelists.com/pages/events',
      children: experienceLinks,
    },
    { label: 'Catering', name: 'catering-search-address' },
    {
      label: 'Gifts',
      path: 'https://pastaevangelists.com/pages/gifts',
      children: giftMenuLinks,
    },
  ];
</script>
